import React, { useMemo, useState } from 'react';
import { ExpressCheckoutElement as StripeExpressCheckoutElement } from '@stripe/react-stripe-js';
import { isFuture, addDays, addWeeks, addMonths, addYears } from 'date-fns';
import useCheckoutState, { checkoutStates } from '../../hooks/useCheckoutState';
import useCartCost from '../../hooks/useCartCost';
import useCheckoutValidation from '../../hooks/useCheckoutValidation';
import useCheckoutHelpers from '../../hooks/useCheckoutHelpers';
import useRecaptcha from '../../hooks/useRecaptcha';
import useSelectedPrice from '../../hooks/useSelectedPrice';
import { isRecurringPurchase as isRecurringPurchaseGate } from '../../lib/multiPrice';
import getPriceBillingConfig from '../../lib/getPriceBillingConfig';
import { PAGE_TYPES } from '../../enums/page-types';
import getSellerDomain from '../../lib/getSellerDomain';
import getThemeLabels from '../../lib/getThemeLabels';
import useGuestsBeforePay from '../../lib/guests/useGuestsBeforePay';
import reportValidityOutsideGuests from '../../lib/guests/reportValidityOutsideGuests';
import clsx from 'clsx';

// Helper function to convert weekly intervals to daily for Apple Pay compatibility
const convertIntervalForApplePay = (interval, intervalCount) => {
  if (interval === 'week') {
    return {
      intervalUnit: 'day',
      intervalCount: intervalCount * 7,
    };
  }
  return {
    intervalUnit: interval,
    intervalCount: intervalCount,
  };
};

const ExpressCheckoutElement = ({ mode = 'express', onReady = () => {} }) => {
  const {
    formRef,
    checkout,
    product,
    setState,
    setFormError,
    seller,
    theme,
    currencyLocale,
    payWhatYouWantPrice,
    isPreview,
    isTesting,
    isTestingPayments,
  } = useCheckoutState();
  const { totalDueAmount, recurringTotalAmount, recurringProcessingFeeContribution } =
    useCartCost();

  const {
    validateMinimumChargeAmount,
    validateHasStock,
    validateVariants,
    validateTickets,
    validateOrderBumps,
  } = useCheckoutValidation();
  const { createPayment, createSubscription } = useCheckoutHelpers();
  const { executeRecaptcha } = useRecaptcha();
  const selectedPrice = useSelectedPrice();
  // Shared gate (lib/multiPrice): recurring follows the selected price,
  // legacy products fall back to product.type.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type via isRecurringPurchaseGate)
  const isRecurringPurchase = isRecurringPurchaseGate(selectedPrice, product);
  const [isReady, setIsReady] = useState(false);
  const { showGuests, guestsReadyToPay } = useGuestsBeforePay();

  const labels = getThemeLabels(theme);

  const handleExpressCheckoutClick = async event => {
    try {
      // Validate form
      if (mode !== 'express') {
        // Attendee inputs are checked by Save attendee, so they must not block the wallet here.
        const formIsValid = showGuests
          ? reportValidityOutsideGuests(formRef.current)
          : !formRef.current || formRef.current.reportValidity();
        if (!formIsValid) return;
      }

      // Validate purchase
      validateVariants();
      validateTickets();
      validateOrderBumps();
      if (!guestsReadyToPay()) return;
      // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.currency)
      validateMinimumChargeAmount(payWhatYouWantPrice, product.currency, currencyLocale);
      validateHasStock();

      const options = {};
      // Update payment information with current total
      if (checkout.type === PAGE_TYPES.CHECKOUT) {
        options.paymentDetails = {
          total: {
            label: product.title,
            amount: totalDueAmount,
          },
        };
      } else if (checkout.type === PAGE_TYPES.EVENT) {
        options.paymentDetails = {
          total: {
            label: checkout.title,
            amount: totalDueAmount,
          },
        };
      }

      // Resolve the click event with the options
      event.resolve(options);
    } catch (error) {
      setFormError(error.message);
      setState(checkoutStates.INITIAL);
    }
  };

  const handleExpressCheckoutConfirm = async event => {
    try {
      // Get a fresh reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('checkout');

      if (isRecurringPurchase) {
        await createSubscription({ recaptchaToken, stripePaymentMethodEvent: event });
      } else {
        await createPayment({ recaptchaToken, stripePaymentMethodEvent: event });
      }
    } catch (error) {
      setFormError(error.message);
      setState(checkoutStates.INITIAL);
    }
  };

  // Recurring config comes from the selected price on multi-price products
  // (each price carries its own interval/trial/start-date); legacy products
  // keep the product-level fields. Both shapes resolve through
  // getPriceBillingConfig (nested recurring/paymentPlan, flat fallback).
  const { interval, intervalCount, trialPeriodDays, startDate, planIterations } =
    (isRecurringPurchase && getPriceBillingConfig(selectedPrice ?? product)) || {};
  const managementURL = `${getSellerDomain(seller)}/portal`;
  const productTitle = product.title;

  // react-stripe-js compares Date options by reference: rebuilding this
  // object every render sends Stripe an element.update() every render.
  const recurringPaymentRequest = useMemo(() => {
    if (!isRecurringPurchase) return null;

    function getRecurringPaymentStartDate() {
      if (startDate && isFuture(new Date(startDate))) {
        return new Date(startDate);
      }

      if (trialPeriodDays) {
        return addDays(new Date(), trialPeriodDays);
      }

      return null;
    }

    function getPaymentPlanEndDate() {
      const planStart = getRecurringPaymentStartDate() || new Date();
      const totalIntervals = intervalCount * planIterations;

      switch (interval) {
        case 'day':
          return addDays(planStart, totalIntervals);
        case 'week':
          return addWeeks(planStart, totalIntervals);
        case 'month':
          return addMonths(planStart, totalIntervals);
        case 'year':
          return addYears(planStart, totalIntervals);
        default:
          return planStart;
      }
    }

    function getTrialEndDate() {
      return addDays(new Date(), trialPeriodDays);
    }

    const applePayInterval = convertIntervalForApplePay(interval, intervalCount);

    const recurringPaymentStartDate = getRecurringPaymentStartDate();

    return {
      paymentDescription: productTitle,
      regularBilling: {
        amount: recurringTotalAmount + (recurringProcessingFeeContribution?.amount ?? 0),
        label: productTitle,
        ...(recurringPaymentStartDate && { recurringPaymentStartDate }),
        ...(planIterations && {
          recurringPaymentEndDate: getPaymentPlanEndDate(),
        }),
        recurringPaymentIntervalUnit: applePayInterval.intervalUnit,
        recurringPaymentIntervalCount: applePayInterval.intervalCount,
      },
      ...(trialPeriodDays && {
        trialBilling: {
          amount: 0,
          label: productTitle,
          recurringPaymentEndDate: getTrialEndDate(),
          recurringPaymentIntervalUnit: 'day',
          recurringPaymentIntervalCount: trialPeriodDays,
        },
      }),
      managementURL,
    };
  }, [
    isRecurringPurchase,
    interval,
    intervalCount,
    trialPeriodDays,
    startDate,
    planIterations,
    recurringTotalAmount,
    recurringProcessingFeeContribution,
    productTitle,
    managementURL,
  ]);

  const disableExpressCheckout = isTesting && !isTestingPayments && isPreview;

  return (
    <div
      className={clsx(
        'cp_stripe-express-checkout-element',
        'bg-[var(--cp_checkout-form-expressCheckout-stripeExpressCheckoutElement-background-color)]',
        'text-[var(--cp_checkout-form-expressCheckout-stripeExpressCheckoutElement-color)]',
        'mb-4',
      )}
      style={{ display: isReady ? 'block' : 'none' }}
    >
      <div
        className={clsx({
          'relative cursor-not-allowed after:absolute after:inset-0 after:z-10 after:cursor-not-allowed':
            disableExpressCheckout,
        })}
      >
        <StripeExpressCheckoutElement
          options={{
            ...(seller.displayName && {
              business: {
                name: seller.displayName,
              },
            }),
            emailRequired: true,
            billingAddressRequired: true,
            buttonType: {
              applePay: 'plain',
              googlePay: 'plain',
            },
            buttonTheme: {
              applePay: checkout.buttonBackgroundColor === 'white' ? 'white' : 'black',
              googlePay: checkout.buttonBackgroundColor === 'white' ? 'white' : 'black',
              paypal: checkout.buttonBackgroundColor === 'white' ? 'white' : 'black',
            },
            layout: {
              overflow: 'never',
            },
            paymentMethods: {
              applePay: 'always',
              googlePay: 'always',
              link: 'auto',
              paypal: 'auto',
              amazonPay: 'auto',
            },
            ...(recurringPaymentRequest && {
              applePay: {
                recurringPaymentRequest,
              },
            }),
          }}
          onClick={handleExpressCheckoutClick}
          onConfirm={handleExpressCheckoutConfirm}
          onReady={event => {
            if (!event.availablePaymentMethods) {
              setIsReady(false);
            } else {
              setIsReady(true);
              onReady();
            }
          }}
        />
      </div>

      {mode === 'default' && theme.elements?.checkout?.form?.orPayWithCard?.enabled !== false && (
        <div className={clsx('cp_pay-with-card-wrapper flex items-center py-2 mt-4 gap-2')}>
          <hr
            className={clsx(
              'cp_pay-with-card-divider h-px flex-1 border-none',
              'bg-[var(--cp_checkout-form-orPayWithCard-border-color)]',
            )}
          />

          <p
            className={clsx(
              'cp_pay-with-card-text whitespace-nowrap text-center',
              'text-[var(--cp_checkout-form-orPayWithCard-color)]',
              '[font-size:var(--cp_font-body-size-sm)]',
              '[line-height:var(--cp_font-body-line-height-sm)]',
            )}
          >
            {labels.orPayWithCardText}
          </p>

          <hr
            className={clsx(
              'cp_pay-with-card-divider h-px flex-1 border-none',
              'bg-[var(--cp_checkout-form-orPayWithCard-border-color)]',
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ExpressCheckoutElement;
